/* eslint-disable react-refresh/only-export-components */
import { useContext, useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/utils/cn';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { useDragToClose } from '@/hooks/useDragToClose';
import { useCheckoutDrawerBottomInset } from '@/hooks/useCheckoutDrawerBottomInset';
import { getDragTransform } from '@/utils/drag';
import { lockBodyScroll, unlockBodyScroll } from '@/utils/scroll';
import { CHECKOUT_DRAWER_BOTTOM_GAP_PX } from '@/utils/checkoutDrawerBottomInset';
import { CheckoutContext } from '@/context/CheckoutContext';

interface CheckoutBottomSheetProps {
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * One slide-up per checkout session (= one page load). The boot skeleton and
 * the live view mount two DIFFERENT sheet instances back-to-back; only the
 * first should play the entrance slide, otherwise the second re-slides from the
 * bottom and the hand-off looks like the sheet bounces. Resets naturally on the
 * next page load (each checkout opens in a fresh iframe).
 */
let hasSheetOpened = false;

/** Test-only escape hatch so entrance-animation assertions start from a clean slate. */
export const __resetSheetOpenedForTests = (): void => {
  hasSheetOpened = false;
};

/**
 * Apple/Google-Pay-style bottom sheet used to present a whole template view
 * (see `PreferredPaymentView`). Purpose-built rather than reusing
 * `Common/Drawer` (which forces inner padding and is owned by the viewer
 * sub-screens) so the wrapped view keeps full control of its layout and
 * `Common/Drawer` stays untouched.
 *
 * Scope: PREFERRED checkout swipe-up presentation ONLY. Browser-chrome bottom
 * inset (`useCheckoutDrawerBottomInset`) must never be copied onto
 * `Common/Drawer` / `ScreenDrawer` / Modal — those keep `bottom-0` / existing
 * layout so saved-card / add-card sub-drawers and full-screen modals are
 * unaffected.
 *
 * Layering (matches `Common/Modal` + `Common/Drawer`):
 *   - Root: `fixed inset-0 z-50`, no transform.
 *   - Backdrop: dimmed game behind (`bg-black/50`), tap → `onClose`. Rendered
 *     at full opacity IMMEDIATELY for the entrance because the drawer's boot
 *     loader already establishes the same dim (see `PreferredPaymentSkeleton`
 *     and `#boot-skeleton` in `index.html`) — so only the panel slides and
 *     there is no dim re-fade flicker across the loader → live-view hand-off.
 *   - Panel: floats a little off each side; `bottom` is the
 *     `useCheckoutDrawerBottomInset()` value so mobile browser toolbars
 *     (Chrome + Safari) that overlay the checkout iframe cannot cover the
 *     last CTA row. Hugs content up to `90dvh` (capped by the same inset).
 *     Flex column — consumers own the scroll split (scrollable Details +
 *     pinned pay / "Use other" footer).
 *   - `prefers-reduced-motion` disables the slide transition.
 *
 * IMPORTANT: full-screen overlays (3DS, processing, redirect, success) must
 * render as SIBLINGS of this sheet in the view — never inside it — so their
 * own `fixed inset-0` covers the full viewport and is not clipped by the
 * panel's `overflow-hidden` / transform.
 */
export const CheckoutBottomSheet = ({ onClose, children, className }: CheckoutBottomSheetProps) => {
  const [reducedMotion] = useState(prefersReducedMotion);
  const context = useContext(CheckoutContext);
  const {
    payment: { isInPaymentProcess, isExpressPaymentInProcess },
  } = context;
  const isPaymentInFlight = isInPaymentProcess || isExpressPaymentInProcess;

  // Skip the entrance slide ONLY when: a previous sheet in this session already
  // played it (live boot-loader → live view hand-off is not applicable now that
  // the boot loader is a plain spinner, but keeping this guard makes the sheet
  // safe if two sheets ever mount back-to-back in one session), or the user
  // prefers reduced motion. The pre-React `#boot-skeleton` is intentionally NOT
  // consulted anymore: under the current design that loader is a centered
  // spinner (NOT a pre-sliding sheet), so the React sheet OWNS the single
  // slide-up and must always play it on first mount.
  const [skipEntrance] = useState(() => hasSheetOpened || reducedMotion);
  const [isOpen, setIsOpen] = useState(skipEntrance);
  const { dragState, handleMouseDown, handleTouchStart } = useDragToClose({ onClose });
  const sheetRootRef = useRef<HTMLDivElement>(null);
  // Checkout-drawer-only inset — Common/Drawer must not use this hook.
  const bottomInset = useCheckoutDrawerBottomInset(sheetRootRef);
  // Own token so a sibling `Drawer`/`ScreenDrawer` closing (which releases ITS
  // token) can't unlock body scroll while this sheet is still open. See
  // `scroll.ts` for the ref-counted lock rationale.
  const scrollLockId = useId();

  useEffect(() => {
    lockBodyScroll(scrollLockId);
    hasSheetOpened = true;

    if (skipEntrance) {
      return () => {
        unlockBodyScroll(scrollLockId);
      };
    }

    // Defer to the next frame so the panel first paints at `translate-y-full`
    // and then transitions up — otherwise it mounts already open with no slide.
    const raf = requestAnimationFrame(() => setIsOpen(true));

    return () => {
      cancelAnimationFrame(raf);
      unlockBodyScroll(scrollLockId);
    };
  }, [skipEntrance, scrollLockId]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isDragging = dragState.isDragging;
  const opened = reducedMotion || isOpen;

  return (
    <div ref={sheetRootRef} data-testid="checkout-bottom-sheet" className="fixed inset-0 z-50">
      {/* Backdrop is at full opacity from the first paint — the boot loader
          (`PreferredPaymentSkeleton` and pre-React `#boot-skeleton`) already
          dimmed the game behind at the same `bg-black/50`, so fading in would
          be a visible re-dim across the hand-off. Only the panel slides. */}
      <div
        data-testid="checkout-bottom-sheet-backdrop"
        className="absolute inset-0 bg-black/50"
        onClick={handleBackdropClick}
      />

      <div
        data-testid="checkout-bottom-sheet-panel"
        className={cn(
          // `bottom` / `maxHeight` come from inline styles driven by
          // `useCheckoutDrawerBottomInset` — a hard-coded `bottom-2` docks the
          // card under mobile browser toolbars that overlay the checkout iframe.
          'absolute inset-x-2 flex flex-col overflow-hidden',
          'rounded-[28px] bg-white shadow-2xl shadow-gray-900/20',
          // iOS-style entrance easing — mirrors `bs-sheet-in` in `index.html`
          // so the drawer's slide feels the same wherever it originates.
          !reducedMotion && !isDragging && 'transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]',
          !isDragging && (opened ? 'translate-y-0' : 'translate-y-full'),
          className
        )}
        style={{
          bottom: bottomInset,
          maxHeight: `min(90dvh, calc(100dvh - ${bottomInset}px - ${CHECKOUT_DRAWER_BOTTOM_GAP_PX}px))`,
          ...(isDragging ? { transform: getDragTransform(dragState) } : null),
        }}
      >
        {/* Invisible drag-to-close surface: the swipe-down-to-dismiss LOGIC is
            kept (drag handlers below), but the visible gray grab-handle pill —
            the bit that read as a "drawer" — is removed per design. `z-30` keeps
            it above the content so a drag from the top still dismisses. */}
        <div
          data-testid="checkout-bottom-sheet-handle"
          className="absolute inset-x-0 top-0 z-30 flex h-8 shrink-0 cursor-grab touch-none select-none active:cursor-grabbing"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />

        {/* X close button OVERLAYS the content (absolute, top-right). Rendered
            AFTER the drag surface so, at the same `z-30`, it stacks on top and
            captures the tap (→ `onClose`) instead of starting a drag. A fixed
            dark icon (not the shared `XButton`, which derives its color from the
            hero-aware `useDetailsContentColor` and is gated by `shouldHideXButton`)
            so it stays visible on the sheet's always-white background regardless
            of publisher theme. The consuming view leaves top padding for it
            (see `PreferredPaymentDrawerView`). */}
        <button
          type="button"
          data-testid="checkout-bottom-sheet-close"
          aria-label="Close"
          onClick={onClose}
          disabled={isPaymentInFlight}
          className={cn(
            'absolute right-4 top-6 z-30 flex h-7 w-7 items-center justify-center rounded-full text-gray-800 transition-colors hover:bg-gray-100 active:bg-gray-200',
            isPaymentInFlight && 'invisible pointer-events-none'
          )}
        >
          <FontAwesomeIcon icon="fa-solid fa-xmark" className="text-lg" />
        </button>

        {/* Flex column (NOT a scrollport): the drawer view pins the pay CTA +
            "Use other" footer and scrolls only Details, so expanding the
            breakdown cannot push the escape hatch below the fold. */}
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
      </div>
    </div>
  );
};
